import { useState } from "react";
import { Box, Button, Checkbox, FormControlLabel, TextField, Typography } from "@mui/material";
import { toast } from "react-toastify";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import { useCreateOrderSerialMutation } from "app/api/orders";
import { useOrderContext } from "context/OrderContext";
import LargeDialog from "components/LargeDialog";
import { ExpandLess } from "@mui/icons-material";
import { Product } from "models/Product";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { useAuthContext } from "features/auth/AuthContext";
import { UserRole } from "features/auth/types/user";

type Props = {
  onClose?: () => void;
  parent: Product;
};

const CreateChildSerialDialog: React.FC<Props> = ({ onClose, parent }) => {
  const order = useOrderContext();
  const { hasRole } = useAuthContext();
  const canEditSerial = hasRole([
    UserRole.ADMIN,
    UserRole.USER_ADMIN,
    UserRole.SALES,
    UserRole.INTERNAL_INSTALLER,
    UserRole.EXTERNAL_INSTALLER,
  ]);

  const [createSerial, { isLoading: isEditing }] = useCreateOrderSerialMutation();
  const [showAllFields, setShowAllFields] = useState<boolean>(false);

  // Variables for new serial form
  const [description, setDescription] = useState<string>("");
  const [serialNumber, setSerialNumber] = useState<string>("");
  const [macAddressWired, setMacAddressWired] = useState<string>("");
  const [ethernetPort, setEthernetPort] = useState<string>("");
  const [theftMarking, setTheftMarking] = useState<string>("");
  const [macAddressWifi, setMacAddressWifi] = useState<string>("");
  const [ipAddress, setIpAddress] = useState<string>("");
  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [assetNumber, setAssetNumber] = useState<string>("");
  const [firmware, setFirmware] = useState<string>("");
  const [other, setOther] = useState<string>("");
  const [doa, setDoa] = useState<boolean>(false);

  const isNoFieldEmpty = Boolean(
    macAddressWired &&
    ethernetPort &&
    theftMarking &&
    macAddressWifi &&
    ipAddress &&
    username &&
    password &&
    assetNumber &&
    firmware &&
    other
  );

  const handleCreateSerial = async () => {
    const checklist = order.checklists?.find((c) => c.roomName === parent.room);

    if (parent.room && !checklist) {
      toast.error(
        `Kunde inte skapa underprodukt. Huvudproduktens checklista '${parent.room}' hittades inte på ordern`
      );
      console.error(
        `Error creating child product. Parents' checklist '${parent.room}' not found on order '${parent.orderNo}'`
      );
      onClose?.();
      return;
    }

    if (!parent.orderNo) {
      toast.error(`Kunde inte skapa underprodukt. Huvudproduktens har ingen order`);
      console.error(`Error creating child product. Parents product has no order`);
      onClose?.();
      return;
    }

    createSerial({
      // Inherited from parent
      orderNo: parent.orderNo,
      checklistId: checklist?.id,
      parentId: parent.id,
      rowNo: parent.rowNo || 0,
      // Instance fields
      description: description,
      serial: serialNumber,
      macAddressWired: macAddressWired,
      macAddressWifi: macAddressWifi,
      ethernetPort: ethernetPort,
      theftMarking: theftMarking,
      ip: ipAddress,
      userName: username,
      password: password,
      firmware: firmware,
      assetNo: assetNumber,
      other: other,
      doa: doa,
    })
      .unwrap()
      .then(() => {
        toast.success("Underprodukten har skapats!");
        onClose?.();
      })
      .catch(() => toast.error("Något gick fel när underprodukten skulle skapas"));
  };

  return (
    <LargeDialog
      onClose={() => onClose?.()}
      onSubmit={(e) => {
        e.preventDefault();
        handleCreateSerial();
      }}
      title="Lägg till underprodukt"
      actions={
        <>
          <Button
            variant="outlined"
            disabled={isEditing}
            startIcon={<CloseIcon />}
            onClick={onClose}
          >
            Avbryt
          </Button>

          <Button
            type="submit"
            variant="contained"
            color="success"
            startIcon={<SaveIcon />}
            loading={isEditing}
          >
            Spara
          </Button>
        </>
      }
    >
      <Box>
        <Typography fontWeight="bold">{parent.item}</Typography>
        <Typography>{parent.description}</Typography>
      </Box>

      <TextField
        variant="filled"
        label="Benämning"
        fullWidth
        required
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />
      <TextField
        variant="filled"
        label="Serienummer"
        disabled={!canEditSerial}
        required
        fullWidth
        value={serialNumber}
        onChange={(e) => setSerialNumber(e.target.value)}
      />
      <TextField
        variant="filled"
        label="MAC-adress"
        fullWidth
        hidden={!showAllFields && !macAddressWired}
        value={macAddressWired}
        onChange={(e) => setMacAddressWired(e.target.value)}
      />
      <TextField
        variant="filled"
        label="MAC-adress Wi-Fi"
        fullWidth
        hidden={!showAllFields && !macAddressWifi}
        value={macAddressWifi}
        onChange={(e) => setMacAddressWifi(e.target.value)}
      />
      <TextField
        variant="filled"
        label="Nätverksuttag"
        fullWidth
        hidden={!showAllFields && !ethernetPort}
        value={ethernetPort}
        onChange={(e) => setEthernetPort(e.target.value)}
      />
      <TextField
        variant="filled"
        label="Stöldmärkning"
        fullWidth
        hidden={!showAllFields && !theftMarking}
        value={theftMarking}
        onChange={(e) => setTheftMarking(e.target.value)}
      />
      <TextField
        variant="filled"
        label="IP-adress (om fast)"
        fullWidth
        hidden={!showAllFields && !ipAddress}
        value={ipAddress}
        onChange={(e) => setIpAddress(e.target.value)}
      />
      <TextField
        variant="filled"
        label="Användarnamn"
        fullWidth
        hidden={!showAllFields && !username}
        value={username}
        onChange={(e) => setUsername(e.target.value)}
      />
      <TextField
        variant="filled"
        label="Lösenord"
        fullWidth
        hidden={!showAllFields && !password}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />
      <TextField
        variant="filled"
        label="Asset-nummer"
        fullWidth
        hidden={!showAllFields && !assetNumber}
        value={assetNumber}
        onChange={(e) => setAssetNumber(e.target.value)}
      />
      <TextField
        variant="filled"
        label="Firmware-version"
        fullWidth
        hidden={!showAllFields && !firmware}
        value={firmware}
        onChange={(e) => setFirmware(e.target.value)}
      />
      <TextField
        variant="filled"
        label="Övrigt"
        fullWidth
        hidden={!showAllFields && !other}
        value={other}
        onChange={(e) => setOther(e.target.value)}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={doa}
            onChange={(e) => {
              setDoa(e.target.checked);
            }}
          />
        }
        label="DOA"
        labelPlacement="start"
        sx={{ ml: 0 }}
      />

      <Button
        hidden={isNoFieldEmpty}
        startIcon={showAllFields ? <ExpandLess /> : <ExpandMore />}
        onClick={() => setShowAllFields(!showAllFields)}
        sx={{ mt: -1, mb: -2 }}
      >
        {showAllFields ? "Dölj tomma fält" : "Visa fler fält"}
      </Button>
    </LargeDialog>
  );
};

export default CreateChildSerialDialog;
